import { redirect } from "@tanstack/react-router";
import { fetchAuthSession } from "aws-amplify/auth";
import type { ShellHandle } from "@observe-tech/mfe-shell-api";
import { createAuthApi } from "./auth";

// The router renders while main.tsx is still deciding how the session boots,
// and an offline boot spends up to the probe timeout finding out. A guard that
// read the offline store before then would see "online" and fail the route.
let markSettled: () => void = () => {};
const settled = new Promise<void>((resolve) => {
  markSettled = resolve;
});

export function settleBoot(): void {
  markSettled();
}

export function bootSettled(): Promise<void> {
  return settled;
}

function hasOfflineSession(now: number = Date.now()): boolean {
  const shell = (window as unknown as { __OBSERVE_SHELL__?: ShellHandle })
    .__OBSERVE_SHELL__;
  if (shell?.offline?.get?.() !== true) return false;
  const expiresAt = shell.offlineExpiresAt?.get?.();
  return typeof expiresAt === "number" && now < expiresAt;
}

export interface SessionGuardDeps {
  hasTokens(): Promise<boolean>;
  bootSettled(): Promise<void>;
  hasOfflineSession(): boolean;
  hasEmergencySession?(): boolean;
}

const defaultDeps: SessionGuardDeps = {
  hasTokens: async () => !!(await fetchAuthSession()).tokens,
  bootSettled,
  hasOfflineSession: () => hasOfflineSession(),
  hasEmergencySession: () => createAuthApi().emergencyRole !== null,
};

export async function requireSession(
  deps: SessionGuardDeps = defaultDeps,
): Promise<void> {
  if (deps.hasEmergencySession?.()) return;
  let signedIn: boolean;
  try {
    signedIn = await deps.hasTokens();
  } catch (e) {
    // Cognito unreachable is not a missing session. Once the boot has settled,
    // an offline session with time left on it is the answer; without one the
    // error surfaces exactly as it did before offline mode.
    if (e instanceof Error && e.name === "NetworkError") {
      await deps.bootSettled();
      if (deps.hasOfflineSession()) return;
    }
    if (e instanceof Error && e.name !== "Error") throw e;
    throw redirect({ to: "/login" });
  }
  if (!signedIn) throw redirect({ to: "/login" });
}
